import { useState, useCallback } from "react";
import {
  Checkbox,
  Col,
  Divider,
  Form,
  Input,
  message,
  Row,
  Typography,
} from "antd";
import {
  UserOutlined,
  MailOutlined,
  LockOutlined,
  BankOutlined,
  IdcardOutlined,
  GlobalOutlined,
} from "@ant-design/icons";
import Button from "../../../components/generic/Button";
import VerifyOtp from "./VerifyOtp";
import { StyledSignupContainer } from "./styles";
import httpClient from "../../../api/httpClient";
import { Link } from "react-router-dom";
import { ROUTES } from "../../../constant/routes";
import Select from "../../../components/generic/Select";
import { employeesSizeOptions, industryOptions } from "../../../constant";
import { useRegions } from "../../../hooks/useRegions";
import PhoneInput from "../../../components/shared/PhoneInput";

const SignupComponent = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState("signup"); // 'signup', 'otp', 'success'
  const [userEmail, setUserEmail] = useState("");
  const { regionsOptions } = useRegions();

  const handleSignup = useCallback(async (values) => {
    setLoading(true);
    try {
      await httpClient.publicPost("users", values);

      setUserEmail(values.email);

      setStep("otp");

      message.success("OTP sent to your email!");
    } catch (error) {
      console.log("🚀 ~ index.jsx:34 ~ SignupComponent ~ error:", error);
      //@ts-ignore
      message.error(error?.errorMessage || "Signup failed. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  return (
    <StyledSignupContainer className="auth-right">
      {step === "signup" ? (
        <div className="signup-div">
          <div className="right-header">
            <h2>Join Kelu today 🚀</h2>
            <p>Manage multiple business cases in one place</p>
          </div>
          <Form form={form} layout="vertical" onFinish={handleSignup}>
            <Divider>Company Information</Divider>
            <Row gutter={16}>
              <Col xs={24}>
                <Form.Item
                  label="Company Name"
                  name="tenant_name"
                  rules={[
                    { required: true, message: "Please enter company name" },
                  ]}
                >
                  <Input prefix={<BankOutlined />} placeholder="Tenant Name" />
                </Form.Item>
              </Col>

              <Col xs={24}>
                <Form.Item
                  label="Government Registered Company ID"
                  name="gov_registered_id"
                >
                  <Input
                    prefix={<IdcardOutlined />}
                    placeholder="e.g. CRN (UK) or CIN (India)"
                  />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  label="Industry"
                  name="industry"
                  rules={[
                    { required: true, message: "Please select industry" },
                  ]}
                >
                  <Select
                    placeholder="Select Industry"
                    options={industryOptions}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item label="Company Size" name="company_size">
                  <Select
                    placeholder="Select Size"
                    options={employeesSizeOptions}
                  />
                </Form.Item>
              </Col>

              <Col xs={24}>
                <Form.Item
                  label="Primary Region"
                  name="region_id"
                  rules={[{ required: true, message: "Please select region" }]}
                >
                  <Select
                    placeholder="Select Region"
                    suffixIcon={<GlobalOutlined />}
                    options={regionsOptions}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Divider>Workspace Setup</Divider>
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item label="Custom Domain (Optional)" name="domain">
                  <Input placeholder="chat.yourcompany.com" />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item label="Tax ID" name="tax_id">
                  <Input placeholder="e.g. VAT (UK) or GSTIN (India)" />
                </Form.Item>
              </Col>
            </Row>

            <Divider>Owner Details</Divider>

            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  label="First Name"
                  name="first_name"
                  rules={[
                    { required: true, message: "Please enter first name" },
                  ]}
                >
                  <Input prefix={<UserOutlined />} placeholder="First Name" />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  name="last_name"
                  label="Last Name"
                  rules={[{ required: true, message: "Required" }]}
                >
                  <Input prefix={<UserOutlined />} placeholder="Last" />
                </Form.Item>
              </Col>

              <Col xs={24}>
                <Form.Item
                  name="email"
                  label="Email Address"
                  rules={[
                    { required: true, message: "Please enter your email" },
                    { type: "email", message: "Please enter a valid email" },
                  ]}
                >
                  <Input
                    prefix={<MailOutlined />}
                    placeholder="your@email.com"
                  />
                </Form.Item>
              </Col>

              <Col xs={24}>
                <PhoneInput />
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  name="password"
                  label="Password"
                  rules={[
                    { required: true, message: "Please enter your password" },
                    {
                      min: 8,
                      message: "Password must be at least 8 characters",
                    },
                  ]}
                >
                  <Input.Password
                    prefix={<LockOutlined />}
                    placeholder="Enter password"
                  />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  name="confirm_password"
                  label="Confirm Password"
                  dependencies={["password"]}
                  rules={[
                    { required: true, message: "Please confirm your password" },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue("password") === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(
                          new Error("Passwords do not match"),
                        );
                      },
                    }),
                  ]}
                >
                  <Input.Password
                    prefix={<LockOutlined />}
                    placeholder="Confirm password"
                  />
                </Form.Item>
              </Col>

              <Col xs={24}>
                <Form.Item
                  name="terms"
                  valuePropName="checked"
                  rules={[
                    {
                      validator(_, value) {
                        if (value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(
                          new Error(
                            "You must accept the Terms and Conditions to continue",
                          ),
                        );
                      },
                    },
                  ]}
                >
                  <Checkbox>
                    I agree to the{" "}
                    <Typography.Link
                      target="_blank"
                      className="text-primary"
                      href={ROUTES.TERMS}
                    >
                      Terms and Conditions
                    </Typography.Link>{" "}
                    and{" "}
                    <Typography.Link
                      target="_blank"
                      className="text-primary"
                      href={ROUTES.PRIVACY_POLICY}
                    >
                      Privacy Policy
                    </Typography.Link>
                  </Checkbox>
                </Form.Item>
              </Col>

              <Col xs={24}>
                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    className="submit-button"
                    loading={loading}
                  >
                    Create Account
                  </Button>
                </Form.Item>
              </Col>
            </Row>
          </Form>
          <div className="text-gray login-link text-center ">
            Already have an account?{" "}
            <Typography.Link href={ROUTES.SIGNIN} className="text-primary">
              Sign In
            </Typography.Link>
          </div>
        </div>
      ) : step === "otp" ? (
        <VerifyOtp userEmail={userEmail} setStep={setStep} />
      ) : (
        <div className="otp-container">
          <div className="flex-col align-center">
            <div className="success-icon rounded flex-center">✓</div>
            <h2 className="success-title">Account Created!</h2>
            <p className="success-message text-gray">
              Your account has been successfully created and verified.
            </p>

            <Button type="primary" className="verify-button">
              <Link to={ROUTES.SIGNIN}>Go to Login</Link>
            </Button>
          </div>
        </div>
      )}
    </StyledSignupContainer>
  );
};

export default SignupComponent;
